import type { ReactNode } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  CloudDownload,
  Code2,
  GitFork,
  Heart,
  Images,
  Layers,
  PenSquare,
  Server,
  Star,
  Users,
  WifiOff,
} from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

/**
 * `/about` — the full documentation surface for Wikistar: what it is,
 * how to use it, exactly which Nostr specs it's built on, and how to
 * run/develop/contribute to it.
 *
 * This exists so the homepage can be a working surface rather than a
 * pitch (see `Index`). Everything explanatory that used to crowd the
 * homepage hero lives here, expanded into real documentation and split
 * across tabs so each audience — reader, writer, protocol implementer,
 * developer — can go straight to their own section.
 */
export function AboutPage() {
  useSeoMeta({
    title: 'About & documentation — Wikistar',
    description:
      "How Wikistar works: a decentralized encyclopedia on Nostr. Reader and writer guides, the Nostr event kinds and NIPs it's built on, development setup, and how to contribute.",
  });

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-4 py-8">
        <header className="mb-8">
          <div className="mb-4 flex size-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-sm">
            <img src="/logo.svg" alt="" className="size-9" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight">The wiki that has no owner</h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Wikistar is a minimalist encyclopedia built on{' '}
            <ExternalLink href="https://nostr.com/">Nostr</ExternalLink>. Every article is a signed event stored on
            relays instead of a single server — anyone can write, anyone can fork, no one can delete.
          </p>
          <p className="mt-3 text-muted-foreground">
            There are no editors and no votes to remove content. Instead, readers{' '}
            <Star className="inline size-4 -translate-y-0.5 fill-current text-primary" />{' '}
            <strong className="font-semibold text-foreground">star</strong> the versions they trust most. Stars are
            public, signed Nostr reactions, so the best writing rises without anyone controlling the rankings.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            <Button asChild>
              <Link to="/">
                <BookOpen className="size-4" />
                Start reading
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/w/new-article/edit">
                <PenSquare className="size-4" />
                Write an article
              </Link>
            </Button>
          </div>
        </header>

        <Tabs defaultValue="using">
          <TabsList className="flex h-auto w-full flex-wrap justify-start">
            <TabsTrigger value="using">Using Wikistar</TabsTrigger>
            <TabsTrigger value="protocol">Nostr &amp; protocol</TabsTrigger>
            <TabsTrigger value="development">Development</TabsTrigger>
            <TabsTrigger value="contributing">Contributing</TabsTrigger>
          </TabsList>

          {/* ---------------- Using Wikistar ---------------- */}
          <TabsContent value="using" className="mt-8 space-y-10">
            <Section icon={BookOpen} title="Reading">
              <P>
                Reading requires no account and no login. The homepage is a browsing surface: jump straight to an
                article title, or switch between <Em>Recent</Em>, <Em>Most starred</Em>, <Em>Publications</Em>, and your{' '}
                <Em>Offline</Em> library without leaving the page.
              </P>
              <DefList
                items={[
                  [
                    'Versions',
                    'Anyone can publish an article under the same title, so several versions may exist side by side. Wikistar shows the newest by default; the History button lists every author\u2019s version so you can read or star any of them.',
                  ],
                  [
                    'Formats',
                    'Article text may be Djot, AsciiDoc, Markdown, or plain prose. Wikistar detects which it is and renders accordingly. When you\u2019re editing, the Content Format selector (top right of the editor) shows and lets you override which format the editor treats the text as — important because two dedicated editors exist (Djot and AsciiDoc), and a wrong choice affects the toolbar and preview even more than the detected reader format.',
                  ],
                  [
                    'Raw view',
                    'The same dropdown has a "Raw" option showing the entire signed event as JSON — kind, tags, pubkey, and signature — so you can verify exactly what was published.',
                  ],
                  [
                    'Reader mode',
                    'Turns an article into fixed, paginated pages instead of one long scroll. Tap the left or right third of the screen (or use the arrow keys) to turn pages. Built for e-ink e-readers and tablets, where continuous scrolling reads poorly.',
                  ],
                ]}
              />
            </Section>

            <Section icon={PenSquare} title="Writing and forking">
              <P>
                Log in with a Nostr browser extension (NIP-07), a remote signer/bunker (NIP-46), or by generating or
                importing a key directly. Then use <Em>Write</Em> in the header.
              </P>
              <DefList
                items={[
                  [
                    'Editing is forking',
                    'Editing someone else\u2019s article publishes your own version under the same title rather than overwriting theirs — the button says "Fork & edit" for exactly this reason. Nobody can be edit-warred out of their own copy.',
                  ],
                  [
                    'Wikilinks',
                    'Write [[Article Name]] or [[Article Name|display text]]. Before publishing, these expand to the spec-compliant link form, so other Nostr clients read your article with no special support for the double-bracket syntax.',
                  ],
                  [
                    'Editor modes',
                    'Raw edit is a plain textarea; Live edit styles headings, bold, and lists as you type; Split editor shows the raw text beside the exact rendering readers will see.',
                  ],
                  [
                    'Publications',
                    'For anything longer than one article — a book, guide, or documentation set — create a publication: an ordered table of contents assembled from separate section events.',
                  ],
                ]}
              />
            </Section>

            <Section icon={Star} title="Stars, zaps, comments, highlights">
              <P>
                Wikistar has no moderators and no downvotes. Every social signal is additive, public, and signed —
                and none of them can remove anyone's writing.
              </P>
              <DefList
                items={[
                  ['Star', 'A public recommendation. Stars accrue to a specific author\u2019s version, never pooled across different takes on the same title. The /stars leaderboard ranks by them.'],
                  ['Zap', 'A real Bitcoin Lightning tip to the author, if they\u2019ve published a Lightning address. Pay via a connected wallet, a browser wallet, or by scanning the invoice.'],
                  ['Comment', 'Threaded public discussion attached to the exact version you\u2019re reading.'],
                  ['Highlight', 'Select any passage and publish it as a public highlight. Other readers see highlights inline when highlight mode is on.'],
                ]}
              />
            </Section>

            <Section icon={WifiOff} title="Offline reading">
              <P>
                <Em>Save</Em> on any article or publication keeps a full copy in this browser, readable with no relay
                connection — useful on a plane, a flaky connection, or a mostly-offline e-reader. Saved items appear on
                the homepage's <Em>Offline</Em> tab and on your profile.
              </P>
              <P>
                The complete signed event is stored, so an offline copy stays independently verifiable and renders
                exactly like a freshly fetched one. Publications save their index <em>and</em> every section together,
                since an index alone is just a table of contents. Because this lives in browser storage, it's tied to
                this device and browser — not to your Nostr account.
              </P>
            </Section>

            <Section icon={CloudDownload} title="Encrypted draft sync">
              <P>
                Drafts are local by default — they live in this browser, like everything else in local storage. If you
                want them on other devices, use <Em>Sync up</Em> on the drafts page to back them up to Nostr, or <Em>
                Sync down</Em> to pull them onto a new device. Each draft is encrypted to your own key before it
                leaves your browser, so no one — not the relays, not other users — can read them.
              </P>
              <P>
                This is what Nostr's NIP-59 "gift wrap" is for: an unsigned note with your draft inside (a{' '}
                <Code>rumor</Code>), encrypted to your own public key (NIP-44), attested as yours (a{' '}
                <Code>seal</Code>), and wrapped with a random one-time key so nothing about it — not even that a
                draft exists — is public. Publishing a draft deletes the synced copy from the relays.
              </P>
            </Section>

            <Section icon={Server} title="Relays and file servers">
              <P>
                Wikistar has no backend of its own. It reads and writes directly to whichever Nostr relays you
                configure, so the content you see depends on the relays you're connected to. If an article seems
                missing, a different relay set is the usual reason.
              </P>
              <P>
                Open the account menu → <Em>Network</Em> to add or remove relays (with independent read/write toggles)
                and Blossom servers for image uploads. When you're logged in, your relay list is published to Nostr as
                a NIP-65 event so it follows you to other clients.
              </P>
            </Section>

            <Section icon={Images} title="Media library">
              <P>
                The <Link to="/media" className="underline underline-offset-2 hover:text-foreground">Media</Link> page
                is where images, video, audio, and PDFs live. Files are stored <strong className="text-foreground">locally first</strong> —
                in this browser's IndexedDB — and uploaded to Blossom servers only when you choose to upload, or
                automatically when an article that uses them is published.
              </P>
              <P>
                Local-first isn't just an offline convenience: Blossom's blob-listing endpoint is explicitly
                "optional and unrecommended" per BUD-12, so a library genuinely cannot be a live view of server
                contents. Keeping its own index is the only workable model, and doing it locally means it never
                announces your files publicly by default.
              </P>
              <DefList
                items={[
                  [
                    'Tags',
                    'Every item can be tagged freely — tags replaced the folder model, since media often belongs to several categories at once. The filter bar on the Media page offers a consistent vocabulary built from your existing tags.',
                  ],
                  [
                    'Inserting into articles',
                    'The "Insert media" button in the editor opens your library. Local-only files insert as a placeholder link that honestly shows "not uploaded yet" in preview rather than looking fine; at publish time every placeholder is uploaded and rewritten to a real URL, so a signed article never ships a broken link.',
                  ],
                  [
                    'Upload state per server',
                    'The detail view shows exactly where each file lives: this device plus each Blossom server that holds it. You can evict the local copy to reclaim space once it\u2019s safely hosted.',
                  ],
                  [
                    'Alt text',
                    'Nudged, not required — an accessible default is better than a blocked workflow, and an obvious reminder to describe images helps far more readers than a hard requirement that would be bypassed.',
                  ],
                  [
                    'NIP-94 publishing (optional)',
                    'Flipping this on publishes a NIP-94 (kind 1063) file-metadata event for each upload, so other clients and your other devices can find it. It\u2019s off by default because it announces your files on your relays.',
                  ],
                ]}
              />
            </Section>
          </TabsContent>

          {/* ---------------- Nostr & protocol ---------------- */}
          <TabsContent value="protocol" className="mt-8 space-y-10">
            <Section icon={Layers} title="No custom event kinds">
              <P>
                Wikistar introduces <strong className="text-foreground">no new event kinds</strong>. It's built
                entirely from existing, open Nostr specs, so anything published here is readable by any other client
                that implements the same NIPs — and Wikistar can read their content too.
              </P>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="py-2 pr-4 font-semibold">Kind</th>
                      <th className="py-2 pr-4 font-semibold">Spec</th>
                      <th className="py-2 font-semibold">Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    <KindRow kind="30818" spec="NIP-54" href="https://github.com/nostr-protocol/nips/blob/master/54.md">
                      Wiki article (addressable, Djot content)
                    </KindRow>
                    <KindRow kind="30819" spec="NIP-54" href="https://github.com/nostr-protocol/nips/blob/master/54.md">
                      Wiki redirect
                    </KindRow>
                    <KindRow kind="30040" spec="NKBIP-01" href="https://wikistr.com/nkbip-01">
                      Publication index (table of contents)
                    </KindRow>
                    <KindRow kind="30041" spec="NKBIP-01" href="https://wikistr.com/nkbip-01">
                      Publication section
                    </KindRow>
                    <KindRow kind="7" spec="NIP-25" href="https://github.com/nostr-protocol/nips/blob/master/25.md">
                      Reaction — Wikistar's <strong>star</strong>
                    </KindRow>
                    <KindRow kind="5" spec="NIP-09" href="https://github.com/nostr-protocol/nips/blob/master/09.md">
                      Deletion request — used to <strong>unstar</strong>
                    </KindRow>
                    <KindRow kind="1111" spec="NIP-22" href="https://github.com/nostr-protocol/nips/blob/master/22.md">
                      Threaded comment
                    </KindRow>
                    <KindRow kind="9802" spec="NIP-84" href="https://github.com/nostr-protocol/nips/blob/master/84.md">
                      Public highlight
                    </KindRow>
                    <KindRow kind="9734 / 9735" spec="NIP-57" href="https://github.com/nostr-protocol/nips/blob/master/57.md">
                      Zap request and receipt
                    </KindRow>
                    <KindRow kind="10002" spec="NIP-65" href="https://github.com/nostr-protocol/nips/blob/master/65.md">
                      Relay list metadata
                    </KindRow>
                  </tbody>
                </table>
              </div>
            </Section>

            <Section icon={GitFork} title="Addressable events and versioning">
              <P>
                Articles and publications are <Em>addressable</Em> events (kinds 30000–39999), identified by the
                combination of <Code>kind</Code>, <Code>pubkey</Code>, and a <Code>d</Code> tag — the normalized
                article title. Relays keep only the newest event per combination, so republishing is how an edit works.
              </P>
              <P>
                Because <Code>pubkey</Code> is part of the identity, two authors writing "Bitcoin" produce two
                independent articles that coexist permanently. There is no shared mutable document to fight over, and
                therefore no edit wars — the reason Wikistar treats "edit someone else's article" as "publish your own
                fork."
              </P>
              <P>
                Every social action is likewise scoped to one specific version via its{' '}
                <Code>kind:pubkey:d</Code> coordinate. A star, zap, comment, or highlight on your version of an
                article never silently transfers to someone else's.
              </P>
            </Section>

            <Section icon={Star} title="Stars as a ranking signal">
              <P>
                A star is a plain NIP-25 like reaction (<Code>kind:7</Code>, <Code>content: "+"</Code>) — precisely
                what NIP-54 itself suggests for deciding which article version to display. Unstarring publishes a
                NIP-09 deletion request retracting your own reaction, rather than a negative reaction, since deletion
                is the correct mechanism for withdrawing something you signed.
              </P>
              <P>
                Counts are per distinct author with an active, non-retracted reaction, so re-reacting can't inflate a
                score. The <Link to="/stars" className="underline underline-offset-2 hover:text-foreground">leaderboard</Link>{' '}
                ranks a bounded pool of recently-edited articles — Nostr has no global trending index, so this is a
                client-side ranking over a candidate window rather than an all-time chart.
              </P>
            </Section>

            <Section icon={Code2} title="Content formats and security">
              <P>
                NIP-54 specifies Djot; NKBIP-01 recommends AsciiDoc; in practice authors often use Markdown or plain
                prose regardless. Wikistar detects the format heuristically and renders whichever it actually is,
                falling back to plain text if a parser produces nothing, so a bad guess can never render a blank page.
              </P>
              <P>
                Every renderer maps parsed nodes to React elements with auto-escaped text and{' '}
                <strong className="text-foreground">never uses </strong>
                <Code>dangerouslySetInnerHTML</Code>. Raw-HTML passthrough features are deliberately not implemented,
                so there is no HTML string that could leak through. Every link and image URL passes an https-only
                allowlist before rendering. This matters more than usual on Nostr: private keys live in browser
                storage, so a single cross-site scripting hole would mean permanent key theft.
              </P>
            </Section>
          </TabsContent>

          {/* ---------------- Development ---------------- */}
          <TabsContent value="development" className="mt-8 space-y-10">
            <Section icon={Layers} title="Architecture">
              <P>
                Wikistar is a fully client-side single-page app. There is no application server, no database, and no
                API of its own — the browser talks directly to Nostr relays over WebSocket, and to Blossom servers for
                media. Deployment is therefore just static file hosting.
              </P>
              <DefList
                items={[
                  ['React 19 + TypeScript', 'UI, with strict typing throughout.'],
                  ['TailwindCSS 4 + shadcn/ui', 'Styling and accessible component primitives.'],
                  ['Nostrify', 'Relay pool, queries, and event publishing.'],
                  ['TanStack Query', 'Fetching, caching, and request deduplication for all relay reads.'],
                  ['React Router', 'Client-side routing, including a root-level NIP-19 identifier route.'],
                  ['Vite', 'Dev server and production bundler.'],
                ]}
              />
            </Section>

            <Section icon={Code2} title="Running locally">
              <P>Clone the repository, then:</P>
              <Pre>{`npm install\nnpm run dev`}</Pre>
              <P>Open the printed local URL. Other useful scripts:</P>
              <Pre>{`npm run build   # type-check and build to dist/\nnpm run test    # type-check, lint, unit tests, and build`}</Pre>
              <P>
                <Code>npm run test</Code> is the full gate — it must pass before any change is considered done.
              </P>
              <P>
                Built with <ExternalLink href="https://shakespeare.diy">Shakespeare</ExternalLink> — the AI-powered
                website builder it was developed through. A reminder that the kind of app doesn't dictate the
                tooling: Wikistar implements a fully Nostr-native client, and that choice scales from scaffolding to
                shipping production applications.
              </P>
            </Section>

            <Section icon={Server} title="Project layout">
              <DefList
                items={[
                  ['src/pages/', 'Route components, wired up in AppRouter.tsx.'],
                  ['src/components/', 'UI components; ui/ holds the shadcn/ui primitives.'],
                  ['src/hooks/', 'Data-fetching and state hooks — one per protocol concern (useStars, useZaps, useHighlights, useComments, useOfflineLibrary, …).'],
                  ['src/lib/', 'Pure logic with no React: spec helpers (nip54, nip25, nkbip01, nip84), parsers (djot, asciidoc, markdown), and sanitizers.'],
                  ['NIP.md', 'The authoritative technical write-up of how every spec is used, including implementation notes and known limitations.'],
                  ['CHANGELOG.md', 'Grouped-by-theme history of what landed, plus a forward roadmap.'],
                ]}
              />
              <P>
                The separation matters: anything in <Code>src/lib/</Code> is unit-testable in isolation, and each
                protocol feature keeps its spec logic, its hook, and its UI in predictable places.
              </P>
            </Section>

            <Section icon={WifiOff} title="Storage and state">
              <P>
                Relay reads are cached by TanStack Query. Genuinely local state — theme, relay and Blossom server
                lists, wallet connections, and the offline article/publication library — lives in{' '}
                <Code>localStorage</Code>, with a few deliberate exceptions. Because that is user-writable and may
                hold data from older versions, everything read back out is re-validated before use rather than
                trusted.
              </P>
              <P>
                Two kinds of data use different storage by design. The <strong className="text-foreground">media
                library</strong> stores its file blobs and thumbnails in <Code>IndexedDB</Code> — not
                <Code>localStorage</Code> — because images would blow the ~5–10MB string quota and, in the same
                breach, damage the offline library and drafts. And <strong className="text-foreground">article
                drafts</strong> live in their own localStorage key (<Code>wikistar:article-drafts</Code>) keyed by a
                generated id rather than the article's slug, since a draft may be renamed mid-writing or have no
                title yet.
              </P>
            </Section>
          </TabsContent>

          {/* ---------------- Contributing ---------------- */}
          <TabsContent value="contributing" className="mt-8 space-y-10">
            <Section icon={Users} title="Ways to contribute">
              <P>
                Wikistar is itself an open, forkable project — much like the wiki it powers. The most valuable
                contribution needs no code at all: write articles, fork and improve existing ones, and star the
                versions you find trustworthy so good writing surfaces.
              </P>
              <DefList
                items={[
                  ['Write', 'Create or fork articles and publications. This is what makes the wiki worth reading.'],
                  ['Curate', 'Star the best versions, assemble publications, and add wikilinks so related articles connect.'],
                  ['Report', 'Open an issue for bugs or rough edges, ideally with the article or relay involved.'],
                  ['Code', 'Pull requests are welcome — see the conventions below.'],
                ]}
              />
            </Section>

            <Section icon={Code2} title="Code conventions">
              <DefList
                items={[
                  ['Prefer existing NIPs', 'Reuse an existing spec, even with minor compromises, over inventing a custom kind. Custom kinds fragment the ecosystem. Wikistar currently defines none.'],
                  ['Document protocol work', 'Any change to how a spec is used belongs in NIP.md, including limitations and rejected alternatives.'],
                  ['No dangerouslySetInnerHTML', 'Ever, anywhere. Untrusted event content is rendered exclusively through escaped JSX, and event-sourced URLs go through the sanitizer.'],
                  ['Scope to a version', 'Any new social feature must attach to a specific kind:pubkey:d coordinate, never to a title in the abstract.'],
                  ['Full gate must pass', 'npm run test — type-check, lint, tests, and build — before a change is done.'],
                ]}
              />
            </Section>

            <Section icon={Heart} title="Editorial philosophy">
              <P>
                Two principles explain most design decisions here, and are worth preserving in any contribution:
              </P>
              <P>
                <strong className="text-foreground">Signals are additive, never subtractive.</strong> Stars, zaps,
                comments, and highlights can only ever recommend or annotate. There is deliberately no downvote, no
                flag-for-removal, and no moderator role, because no participant should be able to remove another's
                signed writing.
              </P>
              <P>
                <strong className="text-foreground">Every author keeps their own copy.</strong> Forking rather than
                overwriting is what makes the absence of moderation workable — disagreement produces a second version
                that readers choose between, instead of a conflict someone has to win.
              </P>
            </Section>

            <Separator />

            <Card>
              <CardContent className="flex flex-col items-start gap-4 py-6 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-semibold">Fork this app</p>
                  <p className="text-sm text-muted-foreground">
                    Wikistar is MIT-licensed and editable in the browser.
                  </p>
                </div>
                <a
                  href="https://shakespeare.diy/clone?url=nostr%3A%2F%2Fnpub1nwwaygle53qpeumc64tcn88tg2lgghgwkgw8f67nwe3jrsr9tu0sy0pkje%2Fwikistar"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0"
                >
                  <img src="https://shakespeare.diy/badge.svg" alt="Edit with Shakespeare" className="h-auto" />
                </a>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

/* ---------------- Small presentational helpers ---------------- */

function Section({ icon: Icon, title, children }: { icon: typeof BookOpen; title: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold tracking-tight">
        <Icon className="size-5 text-primary" />
        {title}
      </h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function P({ children }: { children: ReactNode }) {
  return <p className="leading-7 text-muted-foreground">{children}</p>;
}

function Em({ children }: { children: ReactNode }) {
  return <strong className="font-medium text-foreground">{children}</strong>;
}

function Code({ children }: { children: ReactNode }) {
  return <code className="rounded bg-muted px-1.5 py-0.5 text-sm">{children}</code>;
}

function Pre({ children }: { children: string }) {
  return (
    <pre className="overflow-x-auto rounded-lg bg-muted p-4 text-sm">
      <code>{children}</code>
    </pre>
  );
}

/** A term/description list — used heavily here, so it's worth not repeating the markup. */
function DefList({ items }: { items: [string, string][] }) {
  return (
    <dl className="space-y-3">
      {items.map(([term, description]) => (
        <div key={term} className="border-l-2 border-border pl-4">
          <dt className="font-medium">{term}</dt>
          <dd className="mt-0.5 text-sm leading-6 text-muted-foreground">{description}</dd>
        </div>
      ))}
    </dl>
  );
}

function KindRow({
  kind,
  spec,
  href,
  children,
}: {
  kind: string;
  spec: string;
  href: string;
  children: ReactNode;
}) {
  return (
    <tr className="border-b border-border">
      <td className="py-2 pr-4 align-top">
        <Badge variant="secondary" className="font-mono">
          {kind}
        </Badge>
      </td>
      <td className="py-2 pr-4 align-top whitespace-nowrap">
        <ExternalLink href={href}>{spec}</ExternalLink>
      </td>
      <td className="py-2 align-top text-muted-foreground">{children}</td>
    </tr>
  );
}

function ExternalLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary underline underline-offset-2 hover:no-underline"
    >
      {children}
    </a>
  );
}

export default AboutPage;
